import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createTask, updateTask, updateSubtask } from '../services/firestore';
import { FiX, FiCalendar, FiUser, FiFlag, FiLayers, FiClock, FiRepeat } from 'react-icons/fi';
import toast from 'react-hot-toast';
import CustomSelect from '../../../components/ui/CustomSelect';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'es': es };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const PRIORITY_COLORS = { alta: '#ef4444', media: '#3b82f6', baja: '#10b981' };
const RECURRENCE_OPTIONS = [
    { value: 'none', label: 'Sin repetición' },
    { value: 'daily', label: 'Diaria' },
    { value: 'weekdays', label: 'L-V' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensual' },
];

const CustomEvent = ({ event }) => {
    const { assignedUser, isSubtask, task } = event.resource || {};
    const isCompleted = task?.estado === 'finalizado';
    return (
        <div className="flex items-center gap-1.5 overflow-hidden py-px">
            {isCompleted && (
                <div className="w-3.5 h-3.5 rounded-full bg-white/40 flex items-center justify-center shrink-0">
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
            )}
            {!isCompleted && assignedUser && (
                assignedUser.photoURL ? (
                    <img src={assignedUser.photoURL} alt="" className="w-4 h-4 rounded-full object-cover shrink-0 ring-1 ring-white/50" />
                ) : (
                    <div className="w-4 h-4 rounded-full bg-white/30 flex items-center justify-center text-[8px] font-bold text-white shrink-0">
                        {(assignedUser.displayName || assignedUser.email || '?').charAt(0).toUpperCase()}
                    </div>
                )
            )}
            {isSubtask && <span className="text-[9px] opacity-60 shrink-0">↳</span>}
            <span className={`truncate text-[11px] leading-tight font-medium ${isCompleted ? 'line-through opacity-70' : ''}`}>{event.title}</span>
        </div>
    );
};

const parseDate = (fecha) => {
    if (!fecha) return null;
    let dateVal;
    if (typeof fecha === 'object' && fecha.seconds) {
        dateVal = new Date(fecha.seconds * 1000);
    } else {
        dateVal = new Date(fecha);
    }
    return isNaN(dateVal.getTime()) ? null : dateVal;
};

const collectEvents = (tasks, users) => {
    const events = [];
    const addItem = (item, rootTaskId, subtaskPath) => {
        const dateVal = parseDate(item.fechaVencimiento);
        if (!dateVal) return;
        const assignedEmail = item.asignados?.[0];
        const assignedUser = assignedEmail ? (users || []).find(u => u.email === assignedEmail) : null;
        events.push({
            id: subtaskPath ? `${rootTaskId}__${subtaskPath.join('__')}` : rootTaskId,
            title: item.titulo,
            start: dateVal,
            end: dateVal,
            allDay: dateVal.getHours() === 0 && dateVal.getMinutes() === 0,
            resource: { task: item, assignedUser, priority: item.prioridad || 'media', rootTaskId, subtaskPath: subtaskPath || null, isSubtask: !!subtaskPath },
        });
    };
    const walkSubtasks = (subtareas, rootTaskId, parentPath) => {
        (subtareas || []).forEach(sub => {
            const path = [...parentPath, sub.id];
            addItem(sub, rootTaskId, path);
            walkSubtasks(sub.subtareas, rootTaskId, path);
        });
    };
    (tasks || []).forEach(task => {
        addItem(task, task.id, null);
        walkSubtasks(task.subtareas, task.id, []);
    });
    return events;
};

const AddTaskModal = ({ date, sections, users, onClose, onSubmit }) => {
    const [titulo, setTitulo] = useState('');
    const [seccionId, setSeccionId] = useState(sections[0]?.id || '');
    const [assignee, setAssignee] = useState('');
    const [prioridad, setPrioridad] = useState('media');
    const [time, setTime] = useState('');
    const [recurrence, setRecurrence] = useState('none');
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);
    const modalRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!titulo.trim()) return;
        setSaving(true);
        const taskDate = new Date(date);
        if (time) {
            const [h, m] = time.split(':');
            taskDate.setHours(parseInt(h), parseInt(m));
        }
        try {
            const taskData = {
                titulo: titulo.trim(),
                seccionId: seccionId || null,
                fechaVencimiento: taskDate.toISOString(),
                asignados: assignee ? [assignee] : [],
                prioridad,
            };
            if (recurrence !== 'none') taskData.recurrence = recurrence;
            await onSubmit(taskData);
            onClose();
        } catch { setSaving(false); }
    };

    const dateLabel = format(date, "EEEE d 'de' MMMM", { locale: es });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div>
                        <h3 className="text-sm font-bold text-gray-800">Nueva tarea</h3>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <FiCalendar size={12} className="text-blue-500" />
                            <span className="text-xs text-blue-600 font-medium capitalize">{dateLabel}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/80 rounded-lg transition-colors">
                        <FiX size={16} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-3">
                    <input
                        ref={inputRef}
                        type="text"
                        value={titulo}
                        onChange={(e) => setTitulo(e.target.value)}
                        placeholder="Nombre de la tarea..."
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase mb-1">
                                <FiClock size={10} /> Hora
                            </label>
                            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white font-mono" />
                        </div>
                        <div>
                            <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase mb-1">
                                <FiRepeat size={10} /> Repetir
                            </label>
                            <CustomSelect
                                value={recurrence}
                                onChange={setRecurrence}
                                options={RECURRENCE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase mb-1">
                            <FiLayers size={10} /> Sección
                        </label>
                        <CustomSelect
                            value={seccionId}
                            onChange={setSeccionId}
                            options={sections.map(s => ({ value: s.id, label: s.nombre || s.name }))}
                            className="w-full"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase mb-1">
                                <FiUser size={10} /> Asignar
                            </label>
                            <CustomSelect
                                value={assignee}
                                onChange={setAssignee}
                                options={[
                                    { value: '', label: 'Sin asignar' },
                                    ...(users || []).map(u => ({ value: u.email, label: u.displayName || u.email }))
                                ]}
                                placeholder="Sin asignar"
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase mb-1">
                                <FiFlag size={10} /> Prioridad
                            </label>
                            <CustomSelect
                                value={prioridad}
                                onChange={setPrioridad}
                                options={[
                                    { value: 'baja', label: 'Baja' },
                                    { value: 'media', label: 'Media' },
                                    { value: 'alta', label: 'Alta' },
                                ]}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose}
                            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                            Cancelar
                        </button>
                        <button type="submit" disabled={!titulo.trim() || saving}
                            className="flex-1 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50">
                            {saving ? 'Creando...' : 'Crear tarea'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CalendarView = ({ tasks, users }) => {
    const { user } = useAuth();
    const {
        setSelectedTaskId, setSelectedSubtaskPath, setShowTaskDrawer,
        sections, selectedProjectId, selectedFolderId, selectedListId
    } = useTask();

    const [addModal, setAddModal] = useState(null);
    const events = useMemo(() => collectEvents(tasks, users), [tasks, users]);

    const handleSelectEvent = (event) => {
        const { rootTaskId, subtaskPath } = event.resource;
        setSelectedTaskId(rootTaskId);
        setSelectedSubtaskPath(subtaskPath || []);
        setShowTaskDrawer(true);
    };

    const handleSelectSlot = ({ start }) => {
        setAddModal({ date: start });
    };

    const handleCreateTask = async (taskData) => {
        if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) {
            toast.error('Contexto no disponible para crear tarea');
            return;
        }
        try {
            await createTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, taskData);
            toast.success('Tarea creada');
        } catch (error) {
            console.error(error);
            toast.error('Error al crear tarea');
            throw error;
        }
    };

    const handleToggleComplete = async (event) => {
        const { task, rootTaskId, subtaskPath, isSubtask } = event.resource;
        if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) return;
        const newStatus = task.estado === 'finalizado' ? 'planificacion' : 'finalizado';
        try {
            if (isSubtask && subtaskPath) {
                await updateSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, rootTaskId, subtaskPath, { estado: newStatus });
            } else {
                await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, rootTaskId, { estado: newStatus });
            }
            toast.success(newStatus === 'finalizado' ? 'Tarea completada' : 'Tarea reabierta');
        } catch { toast.error('Error al actualizar'); }
    };

    const eventStyleGetter = (event) => {
        const priority = event.resource?.priority || 'media';
        const isCompleted = event.resource?.task?.estado === 'finalizado';
        const backgroundColor = isCompleted ? '#9ca3af' : (PRIORITY_COLORS[priority] || PRIORITY_COLORS.media);
        return {
            style: {
                backgroundColor, borderRadius: '6px', color: 'white', border: 'none',
                fontSize: '12px', padding: '2px 6px', lineHeight: '1.4', cursor: 'pointer',
                opacity: isCompleted ? 0.6 : 1,
            },
        };
    };

    const dayPropGetter = (date) => {
        const isToday = new Date().toDateString() === date.toDateString();
        return { style: { backgroundColor: isToday ? '#eff6ff' : 'transparent' } };
    };

    // Custom wrapper to add toggle complete on double-click
    const EventWrapper = ({ event, children }) => (
        <div onDoubleClick={(e) => { e.stopPropagation(); handleToggleComplete(event); }}
            title="Doble clic para completar/reabrir">
            {children}
        </div>
    );

    return (
        <div className="h-full p-4">
            <div className="bg-white rounded-xl shadow-sm h-full p-4">
                <Calendar
                    localizer={localizer}
                    events={events}
                    defaultView="month"
                    views={['month', 'week', 'day']}
                    culture="es"
                    selectable
                    popup
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    eventPropGetter={eventStyleGetter}
                    dayPropGetter={dayPropGetter}
                    components={{
                        event: CustomEvent,
                        eventWrapper: EventWrapper,
                    }}
                    messages={{
                        next: "Siguiente", previous: "Anterior", today: "Hoy",
                        month: "Mes", week: "Semana", day: "Día", agenda: "Agenda",
                        date: "Fecha", time: "Hora", event: "Tarea",
                        noEventsInRange: "No hay tareas con fecha en este rango.",
                        showMore: total => `+${total} más`,
                    }}
                    style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}
                />
            </div>

            {addModal && (
                <AddTaskModal
                    date={addModal.date}
                    sections={sections}
                    users={users}
                    onClose={() => setAddModal(null)}
                    onSubmit={handleCreateTask}
                />
            )}
        </div>
    );
};

export default CalendarView;
